import { useEffect, useState, useRef, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

/**
 * A custom React hook for managing WebSocket connections using STOMP over SockJS.
 *
 * @typedef {Object} WebSocketConfig
 * @property {string} url - The URL for the WebSocket connection.
 * @property {string} broker - The broker URL for the WebSocket subscription.
 * @property {StompHeader} stompHeader - The STOMP connection headers.
 *
 * @typedef {Object} StompHeader
 * @property {string} login - The username for the STOMP connection.
 * @property {string} passcode - The password for the STOMP connection.
 *
 * @typedef {Object} ConnectionResult
 * @property {Object} data - The most recent data received from the WebSocket.
 * @property {Array} allData - All accumulated data received from the WebSocket.
 * @property {Function} disconnect - Function to disconnect the WebSocket.
 * @property {Function} connect - Function to reconnect the WebSocket.
 * @property {boolean|null} connected - The current connection status (true: connected, false: disconnected, null: unknown).
 *
 * @param {WebSocketConfig} websocket_config - The configuration object for the WebSocket connection.
 * @param {boolean} debugMode - Whether to enable debug logging for the STOMP client.
 * @returns {[Object, Array, Function, Function, boolean|null]} An array containing the current data, all data, disconnect function, connect function, and connection status.
 */
const useWsConnection = (websocket_config, debugMode) => {
  const [data, setData] = useState({});
  const [allData, setAllData] = useState([]);
  const [connected, setConnected] = useState(null);
  const clientRef = useRef(null);

  const onConnect = useCallback(() => {
    setConnected(true);
    clientRef.current.subscribe(websocket_config.broker, (message) => {
      const data = JSON.parse(message.body);
      if (Array.isArray(data)) {
        setAllData(data);
      } else {
        setData(data);
      }
    });
  }, [websocket_config.broker]);

  const onDisconnect = useCallback(() => {
    setConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.deactivate();
    }

    const client = new Client({
      webSocketFactory: () => new SockJS(websocket_config.url),
      connectHeaders: websocket_config.stompHeader,
      debug: debugMode ? (str) => console.log(str) : () => {},
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect,
      onDisconnect,
    });

    client.onStompError = function (frame) {
      console.error('Broker reported error: ' + frame.headers['message']);
      console.error('Additional details: ' + frame.body);
    };

    clientRef.current = client;
    client.activate();
  }, [websocket_config, debugMode, onConnect, onDisconnect]);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.deactivate();
      clientRef.current = null;
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, []);

  return [data, allData, disconnect, connect, connected];
};

export default useWsConnection;
